import React, { useState } from "react";
import { X, Save, AlertCircle } from "lucide-react";

interface Class {
  _id?: string;
  name: string;
  grade: string;
  section?: string;
  academicTerm?: string;
  capacity?: number;
  subjects?: string[];
  students?: string[];
  teacher?: string;
  isActive: boolean;
  branch: string;
}

interface ClassFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (classData: Omit<Class, "_id">) => void;
  initialData?: Class;
  loading?: boolean;
}

const ClassForm: React.FC<ClassFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  loading = false,
}) => {
  const [formData, setFormData] = useState<Omit<Class, "_id">>({
    name: initialData?.name || "",
    grade: initialData?.grade || "",
    section: initialData?.section || "",
    academicTerm: initialData?.academicTerm || "",
    capacity: initialData?.capacity || 30,
    subjects: initialData?.subjects || [],
    students: initialData?.students || [],
    teacher: initialData?.teacher || "",
    isActive: initialData?.isActive ?? true,
    branch: initialData?.branch || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Class name is required";
    }

    if (!formData.grade.trim()) {
      newErrors.grade = "Grade is required";
    }

    if (formData.capacity && formData.capacity < 1) {
      newErrors.capacity = "Capacity must be at least 1";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseInt(value) : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            {initialData ? "Edit Class" : "Add New Class"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
            disabled={loading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Class Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Class Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`input input-bordered w-full ${
                  errors.name ? "input-error" : ""
                }`}
                placeholder="e.g., Mathematics A"
                disabled={loading}
              />
              {errors.name && (
                <p className="text-error text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.name}
                </p>
              )}
            </div>

            {/* Grade */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Grade *
              </label>
              <select
                name="grade"
                value={formData.grade}
                onChange={handleInputChange}
                className={`select select-bordered w-full ${
                  errors.grade ? "select-error" : ""
                }`}
                disabled={loading}
              >
                <option value="">Select Grade</option>
                <option value="Grade 1">Grade 1</option>
                <option value="Grade 2">Grade 2</option>
                <option value="Grade 3">Grade 3</option>
                <option value="Grade 4">Grade 4</option>
                <option value="Grade 5">Grade 5</option>
                <option value="Grade 6">Grade 6</option>
                <option value="Grade 7">Grade 7</option>
                <option value="Grade 8">Grade 8</option>
                <option value="Grade 9">Grade 9</option>
                <option value="Grade 10">Grade 10</option>
                <option value="Grade 11">Grade 11</option>
                <option value="Grade 12">Grade 12</option>
              </select>
              {errors.grade && (
                <p className="text-error text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.grade}
                </p>
              )}
            </div>

            {/* Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Section
              </label>
              <input
                type="text"
                name="section"
                value={formData.section}
                onChange={handleInputChange}
                className="input input-bordered w-full"
                placeholder="e.g., A, B, C"
                disabled={loading}
              />
            </div>

            {/* Academic Term */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Academic Term
              </label>
              <select
                name="academicTerm"
                value={formData.academicTerm}
                onChange={handleInputChange}
                className="select select-bordered w-full"
                disabled={loading}
              >
                <option value="">Select Term</option>
                <option value="2024-2025 Fall">2024-2025 Fall</option>
                <option value="2024-2025 Spring">2024-2025 Spring</option>
                <option value="2025-2026 Fall">2025-2026 Fall</option>
                <option value="2025-2026 Spring">2025-2026 Spring</option>
              </select>
            </div>

            {/* Capacity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Capacity
              </label>
              <input
                type="number"
                name="capacity"
                value={formData.capacity}
                onChange={handleInputChange}
                className={`input input-bordered w-full ${
                  errors.capacity ? "input-error" : ""
                }`}
                placeholder="30"
                min="1"
                disabled={loading}
              />
              {errors.capacity && (
                <p className="text-error text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.capacity}
                </p>
              )}
            </div>

            {/* Teacher */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teacher
              </label>
              <input
                type="text"
                name="teacher"
                value={formData.teacher}
                onChange={handleInputChange}
                className="input input-bordered w-full"
                placeholder="Select teacher"
                disabled={loading}
              />
            </div>
          </div>

          {/* Branch */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Branch
            </label>
            <input
              type="text"
              name="branch"
              value={formData.branch}
              onChange={handleInputChange}
              className="input input-bordered w-full"
              placeholder="Main Branch"
              disabled={loading}
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleCheckboxChange}
              className="checkbox checkbox-primary"
              disabled={loading}
            />
            <label className="text-sm font-medium text-gray-700">
              Active Class
            </label>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <span className="loading loading-spinner loading-sm"></span>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {initialData ? "Update Class" : "Create Class"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClassForm;
